import { useEffect, useRef, useState } from 'react';
import type { Character } from './character.js';
import { VISEMES, type VisemeId } from './visemes.js';

/**
 * El avatar.
 *
 * Se dibuja como SVG en linea, no como imagen ni como video: se mantiene nitido
 * en cualquier pantalla, pesa unos pocos kilobytes y cada pieza (boca, ojos,
 * cejas) es un elemento que se puede animar por separado. Tambien significa que
 * un ilustrador puede sustituir las trayectorias sin tocar la logica.
 *
 * Tres capas de movimiento, de mas a menos visible:
 *   1. Boca      el visema que toca en cada instante, con la apertura modulada
 *                por el volumen real del audio.
 *   2. Parpadeo  cada 3 a 6 segundos, con parpadeos dobles ocasionales.
 *   3. Cabeceo   deriva lenta y continua para que nunca quede congelado.
 */

export interface AvatarProps {
  character: Character;
  /** Visema vigente. */
  viseme: VisemeId;
  /** Volumen instantaneo del audio, 0..1. Modula cuanto se abre la boca. */
  amplitude?: number;
  /** Activa el cabeceo mas marcado mientras habla. */
  speaking?: boolean;
  className?: string;
}

/** Parpadeo: devuelve 1 (ojo abierto) o 0 (cerrado), con ritmo natural. */
function useBlink(): number {
  const [open, setOpen] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];

    const schedule = () => {
      if (cancelled) return;
      // Entre 2.8 y 6.2 segundos, como una persona en reposo.
      const wait = 2800 + Math.random() * 3400;
      timers.push(
        window.setTimeout(() => {
          if (cancelled) return;
          setOpen(0);
          timers.push(
            window.setTimeout(() => {
              if (cancelled) return;
              setOpen(1);
              // Uno de cada cinco parpadeos es doble.
              if (Math.random() < 0.2) {
                timers.push(
                  window.setTimeout(() => {
                    if (cancelled) return;
                    setOpen(0);
                    timers.push(window.setTimeout(() => !cancelled && setOpen(1), 90));
                  }, 130),
                );
              }
              schedule();
            }, 95),
          );
        }, wait),
      );
    };

    schedule();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, []);

  return open;
}

/** Cabeceo lento. Dos senos desfasados dan un movimiento que no se repite. */
function useSway(active: boolean): { x: number; y: number; rotate: number } {
  const [pose, setPose] = useState({ x: 0, y: 0, rotate: 0 });
  const frame = useRef(0);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setPose({ x: 0, y: 0, rotate: 0 });
      return;
    }

    const gain = active ? 1.6 : 1;
    const tick = (time: number) => {
      const t = time / 1000;
      setPose({
        x: Math.sin(t * 0.42) * 2.6 * gain,
        y: Math.sin(t * 0.63 + 1.1) * 1.8 * gain,
        rotate: Math.sin(t * 0.31 + 0.4) * 0.7 * gain,
      });
      frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [active]);

  return pose;
}

export function Avatar({
  character: c,
  viseme,
  amplitude = 0,
  speaking = false,
  className,
}: AvatarProps) {
  const eyeOpen = useBlink();
  const sway = useSway(speaking);
  const shape = VISEMES[viseme];

  // La forma da el visema; el volumen da cuanto se abre. Sin esto la boca
  // parece una marioneta: las formas correctas pero siempre igual de marcadas.
  const drive = 0.55 + 0.45 * Math.min(1, Math.max(0, amplitude));
  const jaw = shape.open * drive * 4.5;

  // La anchura del rostro es el rasgo que mas cambia el parecido, asi que es
  // un dato del personaje y no una constante del dibujo.
  const rx = 74 * c.faceWidth;

  return (
    <svg
      className={className}
      viewBox="0 0 400 400"
      role="img"
      aria-label="Asistente virtual del Centro de Educacion Continua"
    >
      <defs>
        <radialGradient id="halo" cx="50%" cy="38%" r="62%">
          <stop offset="0%" stopColor="var(--halo-a)" />
          <stop offset="100%" stopColor="var(--halo-b)" />
        </radialGradient>
        <clipPath id="clip-eye-left">
          <ellipse cx="172" cy="192" rx="13" ry="9" />
        </clipPath>
        <clipPath id="clip-eye-right">
          <ellipse cx="228" cy="192" rx="13" ry="9" />
        </clipPath>
        <clipPath id="clip-head">
          <ellipse cx="200" cy="190" rx={rx} ry="92" />
        </clipPath>
      </defs>

      <circle cx="200" cy="196" r="186" fill="url(#halo)" />

      <g
        transform={`translate(${sway.x} ${sway.y}) rotate(${sway.rotate} 200 300)`}
        style={{ transition: 'none' }}
      >
        {/* ---- Torso ------------------------------------------------------ */}
        <path d="M18,400 C26,368 112,342 200,342 C288,342 374,368 382,400 L382,400 L18,400 Z" fill={c.suit} />
        <path d="M200,342 C140,342 60,364 26,392 L22,400 L200,400 Z" fill={c.suitShade} opacity="0.55" />
        {/* Camisa y corbata */}
        <path d="M172,346 L200,390 L228,346 L216,340 L200,362 L184,340 Z" fill={c.shirt} />
        {/* Sin corbata la V de la camisa ya lee como cuello abierto. */}
        {c.tie ? <path d="M200,364 L211,380 L206,400 L194,400 L189,380 Z" fill={c.tie} /> : null}
        {/* Solapas */}
        <path d="M172,346 L200,392 L176,400 L136,400 Z" fill={c.suitShade} opacity="0.5" />
        <path d="M228,346 L200,392 L224,400 L264,400 Z" fill={c.suitShade} opacity="0.32" />

        {/* ---- Cuello ----------------------------------------------------- */}
        {/* Ancho y corto, y ensanchando hacia los trapecios: un cuello
            estrecho y recto lee como un tronco pegado a la cabeza. */}
        <path d="M168,268 L232,268 L238,346 Q200,358 162,346 Z" fill={c.skinShade} />
        <path d="M168,270 L232,270 L230,292 Q200,304 170,292 Z" fill={c.skinLine} opacity="0.28" />

        {/* ---- Orejas ----------------------------------------------------- */}
        {/* Justo en el borde del ovalo de la cabeza, no detras. */}
        <ellipse cx={200 - rx} cy="200" rx="11" ry="17" fill={c.skin} />
        <ellipse cx={200 + rx} cy="200" rx="11" ry="17" fill={c.skin} />
        <path
          d={`M${200 - rx},194 Q${205 - rx},200 ${200 - rx},207`}
          fill="none"
          stroke={c.skinLine}
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.6"
        />
        <path
          d={`M${200 + rx},194 Q${195 + rx},200 ${200 + rx},207`}
          fill="none"
          stroke={c.skinLine}
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.6"
        />

        {/* ---- Cabeza ----------------------------------------------------- */}
        <ellipse cx="200" cy="190" rx={rx} ry="92" fill={c.skin} />
        {/* Sombra lateral: da volumen sin recurrir a degradados */}
        <g clipPath="url(#clip-head)">
          <path d="M138,98 Q124,190 146,282 L112,282 L112,98 Z" fill={c.skinShade} opacity="0.5" />
        </g>

        {/* ---- Pelo ------------------------------------------------------- */}
        <g transform={`translate(200 190) scale(${c.faceWidth} 1) translate(-200 -190)`}>
          <Hair character={c} />
        </g>

        {/* ---- Cejas ------------------------------------------------------ */}
        <path
          d="M155,167 Q172,158 190,165 L189,170 Q172,164 156,172 Z"
          fill={c.brow}
        />
        <path
          d="M245,167 Q228,158 210,165 L211,170 Q228,164 244,172 Z"
          fill={c.brow}
        />

        {/* ---- Ojos ------------------------------------------------------- */}
        <Eye side="left" character={c} open={eyeOpen} />
        <Eye side="right" character={c} open={eyeOpen} />

        {/* ---- Nariz ------------------------------------------------------ */}
        <path
          d="M200,196 L194,232 Q200,238 208,232"
          fill="none"
          stroke={c.skinLine}
          strokeWidth="3.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* ---- Vello facial (DEBAJO de la boca) ---------------------------- */}
        {/* Va antes que la boca para que los labios se dibujen encima de la
            barba. Al reves obligaba a recortar un hueco en la barba, que es lo
            que producia el efecto de "barba de correa". */}
        <FacialHair character={c} jaw={jaw} />

        {/* ---- Boca ------------------------------------------------------- */}
        {/* El desplazamiento vertical simula la mandibula al abrirse. */}
        <g transform={`translate(0 ${jaw})`}>
          {/* Cavidad: se ve detras de dientes y lengua cuando la boca abre */}
          <path d={shape.lip} fill="#4A2530" />
          {shape.teeth ? <path d={shape.teeth} fill="#FCFCFA" /> : null}
          {shape.tongue ? <path d={shape.tongue} fill="#C4707E" /> : null}
          <path
            d={shape.lip}
            fill="none"
            stroke={c.lipLine}
            strokeWidth="3.2"
            strokeLinejoin="round"
          />
        </g>

        {/* ---- Gafas (capa superior) --------------------------------------- */}
        {c.glasses ? (
          <g fill="none" stroke={c.glassesFrame} strokeWidth="4">
            <rect x="150" y="178" width="46" height="30" rx="12" fill="#FFFFFF" opacity="0.14" />
            <rect x="150" y="178" width="46" height="30" rx="12" />
            <rect x="204" y="178" width="46" height="30" rx="12" fill="#FFFFFF" opacity="0.14" />
            <rect x="204" y="178" width="46" height="30" rx="12" />
            <path d="M196,191 L204,191" />
            <path d="M150,186 L130,192" />
            <path d="M250,186 L270,192" />
          </g>
        ) : null}
      </g>
    </svg>
  );
}

function Eye({
  side,
  character: c,
  open,
}: {
  side: 'left' | 'right';
  character: Character;
  open: number;
}) {
  const cx = side === 'left' ? 172 : 228;
  const clip = side === 'left' ? 'clip-eye-left' : 'clip-eye-right';
  // El parpado baja hasta tapar el ojo. La transicion corta lo hace creible.
  const lidY = 183 - 21 * open;

  return (
    <g>
      <ellipse cx={cx} cy="192" rx="13" ry="9" fill="#FCFCFA" />
      <g clipPath={`url(#${clip})`}>
        <circle cx={cx} cy="192" r="6.4" fill={c.eye} />
        <circle cx={cx} cy="192" r="3" fill="#1A1A1E" />
        <circle cx={cx - 2.2} cy="189.6" r="1.3" fill="#FFFFFF" opacity="0.9" />
        {/* Parpado */}
        <rect
          x={cx - 16}
          y={lidY - 21}
          width="32"
          height="24"
          fill={c.skin}
          style={{ transition: 'y 70ms linear' }}
        />
      </g>
      {/* Linea de pestanas: solo el borde superior, como en un trazo a mano */}
      <path
        d={`M${cx - 13},191 Q${cx},${183.5} ${cx + 13},191`}
        fill="none"
        stroke={c.skinLine}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.8"
      />
    </g>
  );
}

/**
 * Pelo.
 *
 * Cada estilo es un casquete cerrado cuyo borde exterior sube por encima del
 * ovalo de la cabeza (y = 88, con la coronilla en y = 98). Si el borde exterior
 * se queda por debajo, asoma una franja de piel por encima del pelo y parece
 * una calva mal recortada.
 *
 * El borde interior es la linea de nacimiento del pelo: cuanto mas abajo y mas
 * recta, mas frente despejada.
 */
function Hair({ character: c }: { character: Character }) {
  switch (c.hairStyle) {
    // Entradas: el nacimiento retrocede en las sienes y avanza en el centro.
    case 'entradas':
      return (
        <g>
          <path
            d="M124,178 Q120,88 200,88 Q280,88 276,178 Q272,150 262,140 Q246,148 236,134
               Q218,124 200,126 Q182,124 164,134 Q154,148 138,140 Q128,150 124,178 Z"
            fill={c.hair}
          />
          <path d="M124,178 Q128,150 138,140 Q136,158 134,180 Z" fill={c.hairShade} />
        </g>
      );

    // Peinado hacia un lado, con canas en las sienes.
    case 'peinado':
      return (
        <g>
          <path
            d="M124,180 Q120,88 200,88 Q280,88 276,180 Q272,140 258,128
               Q226,116 172,124 Q140,132 124,180 Z"
            fill={c.hair}
          />
          <path d="M172,124 Q214,108 258,128 Q212,120 176,140 Z" fill={c.hairShade} opacity="0.55" />
          {c.temples ? (
            <>
              <path d="M124,182 Q125,152 138,133 Q131,158 131,184 Z" fill={c.temples} opacity="0.55" />
              <path d="M276,182 Q275,152 262,133 Q269,158 269,184 Z" fill={c.temples} opacity="0.55" />
            </>
          ) : null}
        </g>
      );

    // Cano, con las sienes mas cortas.
    case 'canoso':
      return (
        <g>
          <path
            d="M122,180 Q118,88 200,88 Q282,88 278,180 Q274,136 244,124
               Q200,112 156,124 Q126,136 122,180 Z"
            fill={c.hair}
          />
          <path d="M122,180 Q126,136 156,124 Q136,146 132,182 Z" fill={c.hairShade} />
          <path d="M210,104 Q250,112 266,146 Q244,122 208,116 Z" fill="#EDEDF1" opacity="0.3" />
        </g>
      );

    case 'corto':
    default:
      return (
        <path
          d="M124,178 Q120,88 200,88 Q280,88 276,178 Q270,132 200,124 Q130,132 124,178 Z"
          fill={c.hair}
        />
      );
  }
}

/**
 * Vello facial.
 *
 * Todas las variantes se dibujan como una mancha solida recortada al ovalo de
 * la cabeza (`clip-head`), no como un contorno. Un contorno produce el efecto
 * de "barba de correa"; una mancha recortada se comporta como vello de verdad
 * y encaja sola con cualquier forma de cara.
 */
function FacialHair({ character: c, jaw }: { character: Character; jaw: number }) {
  if (c.facialHair === 'ninguna') return null;

  if (c.facialHair === 'bigote') {
    return (
      <path
        d="M171,247 Q200,237 229,247 Q222,254 200,251 Q178,254 171,247 Z"
        fill={c.beard}
        transform={`translate(0 ${jaw * 0.35})`}
      />
    );
  }

  // El borde superior baja desde las patillas hasta debajo de los pomulos.
  // La barba llena hacia abajo; la boca se dibuja despues, encima.
  const mask =
    c.facialHair === 'incipiente'
      ? 'M116,232 Q158,266 200,266 Q242,266 284,232 L284,320 L116,320 Z'
      : 'M116,208 Q158,250 200,250 Q242,250 284,208 L284,320 L116,320 Z';

  return (
    <g clipPath="url(#clip-head)">
      <path
        d={mask}
        fill={c.beard}
        opacity={c.facialHair === 'incipiente' ? 0.26 : 1}
        transform={`translate(0 ${jaw * 0.35})`}
      />
    </g>
  );
}
